import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import Avatar from "./Avatar";
import { FaChevronLeft, FaChevronRight, FaUserPlus, FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "../lib/toast";

const SuggestedUsers = () => {
  const [users, setUsers] = useState([]);
  const [followedIds, setFollowedIds] = useState(new Set());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [removingIds, setRemovingIds] = useState(new Set());
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSuggested = async () => {
      try {
        const res = await axiosInstance.get("/api/users/suggested/");
        setUsers(res.data);
      } catch (err) {
        console.error("Failed to load suggested users", err);
      }
    };
    fetchSuggested();
  }, []);

  const handleFollow = async (e, userId) => {
    e.stopPropagation();
    setLoadingIds((prev) => new Set(prev).add(userId));
    try {
      await axiosInstance.post(`/api/users/${userId}/follow/`);
      setFollowedIds((prev) => new Set(prev).add(userId));
    } catch (err) {
      if (err.response?.status === 409) {
        setFollowedIds((prev) => new Set(prev).add(userId));
      }
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleRemove = async (e, userId) => {
    e.stopPropagation();
    setRemovingIds((prev) => new Set(prev).add(userId));
    try {
      await axiosInstance.delete(`/api/users/suggested/${userId}/`);
      setUsers((prev) => prev.filter((user) => user.id !== userId));
    } catch (err) {
      console.error("Failed to remove suggested user", err);
      toast.error("Failed to remove suggested user.");
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -200 : 200,
        behavior: "smooth",
      });
    }
  };

  if (users.length === 0) return null;

  return (
    <div>
      <div className="relative">
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 rounded-full p-1.5 shadow-md -ml-2"
        >
          <FaChevronLeft className="w-3 h-3 text-gray-600 dark:text-gray-300" />
        </button>

        <div ref={scrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-2 px-1">
          {users.map((user) => {
            const isFollowed = followedIds.has(user.id);
            const isLoading = loadingIds.has(user.id);
            const isRemoving = removingIds.has(user.id);
            const name = [user.first_name, user.last_name].filter(Boolean).join(" ") || "User";

            return (
              <div
                key={user.id}
                onClick={() => navigate(`/profile/${user.id}`)}
                className="relative w-44 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/50 cursor-pointer hover:shadow-lg transition-shadow p-4 flex flex-col items-center text-center"
              >
                <button
                  onClick={(e) => handleRemove(e, user.id)}
                  disabled={isRemoving || isLoading}
                  aria-label={`Remove ${name} from suggested users`}
                  className="absolute right-3 top-3 h-7 w-7 rounded-full text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRemoving ? "..." : <FaTimes className="mx-auto h-3 w-3" />}
                </button>
                <Avatar user={user} size="lg" />
                <p className="mt-2 text-sm font-semibold text-gray-800 dark:text-white truncate w-full">
                  {name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user.recipes_count} recipe{user.recipes_count !== 1 ? "s" : ""}
                </p>
                {user.mutual_count > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.mutual_count} mutual
                  </p>
                )}
                <div className="mt-2 flex w-full">
                  <button
                    onClick={(e) => !isFollowed && handleFollow(e, user.id)}
                    disabled={isFollowed || isLoading || isRemoving}
                    className={`flex-1 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isFollowed
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-default"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                  >
                    {isFollowed ? (
                      <span className="flex items-center justify-center gap-1">
                        <FaCheck className="w-3 h-3" /> Following
                      </span>
                    ) : isLoading ? (
                      "..."
                    ) : (
                      <span className="flex items-center justify-center gap-1">
                        <FaUserPlus className="w-3 h-3" /> Follow
                      </span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 rounded-full p-1.5 shadow-md -mr-2"
        >
          <FaChevronRight className="w-3 h-3 text-gray-600 dark:text-gray-300" />
        </button>
      </div>
    </div>
  );
};

export default SuggestedUsers;
